/**
 * Client-side transport cache for `taxonomy_items` reads (feed reference data).
 *
 * WHY: taxonomy is near-static reference data (brands, themes, product types,
 * TCG metadata, card conditions/languages/formats, grading companies). Multiple
 * feed components each fetched it independently — a single feed mount fired
 * ~10 GET + ~10 OPTIONS to Supabase with the SAME query duplicated 2-4×
 * concurrently (feed-top-bar + feed-filters + feed-filters-overlay all fetch
 * brand/theme/product_type at once). This module collapses that to ONE fetch
 * per distinct query and persists across tabs.
 *
 * Three layers of dedup, cheapest first:
 *   1. module-level in-memory Map — instant within a session (survives remounts).
 *   2. single-flight — concurrent callers for the same key share ONE in-flight
 *      promise. This is what kills the 2-4× concurrent duplication.
 *      (concurrency-design §4.1 in-process single-flight / request coalescing.)
 *   3. localStorage with TTL — a fresh tab reuses a recent snapshot instead of
 *      refetching all 8 types.
 *
 * Design (hex): the cache orchestration is a pure factory (`makeTaxonomyCache`)
 * with injected deps (fetcher / storage / clock) so single-flight + TTL + corrupt
 * -entry handling are unit-testable without Supabase, the DOM, or React. The
 * default export wires the real Supabase fetcher + real localStorage + Date.now.
 * (hex §1 pure domain / §2 adapters, §6.5 FC/IS "shell loads, core decides".)
 *
 * TRANSPORT-ONLY: consumers keep their own `.map` / dedup / filter projection.
 * This module unifies the SELECT to a superset and returns raw rows — it does
 * NOT reshape what any component renders.
 *
 * Cache-key decision: keyed by (type, parent-filter, limit) — NOT by column set.
 * Every consumer's SELECT is unified to one superset (`id, slug, name, type,
 * sort_order, parent_id`); reference rows are tiny (dozens per type) so fetching
 * a superset once maximizes cache hits (all consumers of `brand-any` share one
 * entry) and keeps the key simple. `is_active = true` is always applied (no
 * consumer wants inactive rows) so it is not part of the key.
 *
 * @see .tondocs/skills/concurrency-design.md §4.1 (in-process single-flight)
 * @see .tondocs/skills/hex.md §6.5 (functional core, imperative shell)
 */

import { createClient } from "@/utils/supabase/client";
import { unreachable } from "@/lib/type-helpers";

// ── Public types ────────────────────────────────────────────────────────────

/** Parent-id filter axis. Mirrors the `parent_id` predicates used at call sites. */
export type TaxonomyParentFilter =
  | { mode: "any" } // no parent_id predicate
  | { mode: "root" } // parent_id IS NULL
  | { mode: "eq"; id: string } // parent_id = id
  | { mode: "in"; ids: string[] }; // parent_id IN (...)

export interface TaxonomyQuery {
  /** `taxonomy_items.type` — e.g. "brand", "theme", "tcg", "card_condition". */
  type: string;
  /** Parent-id predicate. Defaults to `{ mode: "any" }`. */
  parent?: TaxonomyParentFilter;
  /** Optional row cap (some call sites use `.limit(200)` on tcg_set/tcg_rarity). */
  limit?: number;
}

/** Superset row every consumer projects from. */
export interface TaxonomyCacheRow {
  id: string;
  slug: string | null;
  name: string;
  type: string;
  sort_order: number | null;
  parent_id: string | null;
}

/** Real fetch adapter surface — inject a fake in tests. */
export type TaxonomyFetcher = (query: TaxonomyQuery) => Promise<TaxonomyCacheRow[]>;

/** Minimal synchronous key-value store surface (a subset of `localStorage`). */
export interface TaxonomyStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface TaxonomyCacheDeps {
  fetcher: TaxonomyFetcher;
  /** Persistence layer. Omit / null to disable persistence (memory + single-flight only). */
  storage?: TaxonomyStorage | null;
  /** Time-to-live for both memory and persisted entries, ms. */
  ttlMs?: number;
  /** Monotonic-enough clock; injected for TTL tests. */
  now?: () => number;
}

export interface TaxonomyCache {
  get(query: TaxonomyQuery): Promise<TaxonomyCacheRow[]>;
}

// ── Constants ───────────────────────────────────────────────────────────────

/** Superset select — covers id/name/slug/type/sort_order/parent_id across all consumers. */
export const TAXONOMY_SUPERSET_SELECT = "id, slug, name, type, sort_order, parent_id";

/** 1 hour. Reference data changes rarely (infrequent admin edits); 1h staleness is fine. */
const DEFAULT_TTL_MS = 60 * 60 * 1000;

/** Bump the version segment on any change to the persisted shape to invalidate old entries. */
const STORAGE_PREFIX = "kraahl:taxcache:v1:";

// ── Key derivation (pure) ───────────────────────────────────────────────────

function parentKey(parent: TaxonomyParentFilter): string {
  switch (parent.mode) {
    case "any":
      return "any";
    case "root":
      return "root";
    case "eq":
      return `eq:${parent.id}`;
    case "in":
      // Sort so caller ordering doesn't fragment the key.
      return `in:${[...parent.ids].sort().join(",")}`;
    default:
      return unreachable(parent);
  }
}

/** Stable cache key for a query. Exported for tests. */
export function taxonomyCacheKey(query: TaxonomyQuery): string {
  const parent = query.parent ?? { mode: "any" };
  return `${query.type}|${parentKey(parent)}|lim:${query.limit ?? 0}`;
}

// ── Persisted-entry shape ───────────────────────────────────────────────────

interface CacheEntry {
  rows: TaxonomyCacheRow[];
  expires: number;
}

function isCacheEntry(v: unknown): v is CacheEntry {
  return (
    typeof v === "object" &&
    v !== null &&
    Array.isArray((v as { rows?: unknown }).rows) &&
    typeof (v as { expires?: unknown }).expires === "number"
  );
}

// ── Factory (functional core) ───────────────────────────────────────────────

/**
 * Build a taxonomy cache over injected deps. Pure of Supabase/DOM/React —
 * every dep is a parameter, so single-flight, TTL, and corrupt-storage handling
 * are testable with fakes.
 */
export function makeTaxonomyCache(deps: TaxonomyCacheDeps): TaxonomyCache {
  const { fetcher } = deps;
  const storage = deps.storage ?? null;
  const ttlMs = deps.ttlMs ?? DEFAULT_TTL_MS;
  const now = deps.now ?? Date.now;

  const memory = new Map<string, CacheEntry>();
  const inflight = new Map<string, Promise<TaxonomyCacheRow[]>>();

  // Storage helpers degrade gracefully: private mode, quota, corrupt JSON, or a
  // missing/throwing store must never surface to the UI — they read as a miss.
  function readStorage(key: string, nowMs: number): TaxonomyCacheRow[] | null {
    if (!storage) return null;
    try {
      const raw = storage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!isCacheEntry(parsed)) return null;
      if (parsed.expires <= nowMs) return null;
      // Hydrate memory so the next call skips the sync read too.
      memory.set(key, parsed);
      return parsed.rows;
    } catch {
      return null;
    }
  }

  function writeStorage(key: string, entry: CacheEntry): void {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // ignore — quota / private mode / serialization; memory cache still holds.
    }
  }

  return {
    async get(query: TaxonomyQuery): Promise<TaxonomyCacheRow[]> {
      const key = taxonomyCacheKey(query);
      const nowMs = now();

      // 1. fresh in-memory entry → instant.
      const mem = memory.get(key);
      if (mem && mem.expires > nowMs) return mem.rows;

      // 2. an in-flight fetch for this key → coalesce (single-flight).
      const flying = inflight.get(key);
      if (flying) return flying;

      // 3. fresh persisted snapshot (new tab / cold memory) → reuse.
      const persisted = readStorage(key, nowMs);
      if (persisted) return persisted;

      // 4. miss → fetch once, broadcast to all concurrent callers.
      const p = fetcher(query)
        .then((rows) => {
          const entry: CacheEntry = { rows, expires: now() + ttlMs };
          memory.set(key, entry);
          writeStorage(key, entry);
          return rows;
        })
        .finally(() => {
          inflight.delete(key);
        });

      inflight.set(key, p);
      return p;
    },
  };
}

// ── Default adapter (imperative shell) ──────────────────────────────────────

let clientSingleton: ReturnType<typeof createClient> | null = null;
function getBrowserClient() {
  if (!clientSingleton) clientSingleton = createClient();
  return clientSingleton;
}

const supabaseFetcher: TaxonomyFetcher = async (query) => {
  const supabase = getBrowserClient();
  let q = supabase
    .from("taxonomy_items")
    .select(TAXONOMY_SUPERSET_SELECT)
    .eq("type", query.type)
    .eq("is_active", true);

  const parent = query.parent ?? { mode: "any" };
  switch (parent.mode) {
    case "any":
      break;
    case "root":
      q = q.is("parent_id", null);
      break;
    case "eq":
      q = q.eq("parent_id", parent.id);
      break;
    case "in":
      q = q.in("parent_id", parent.ids);
      break;
    default:
      return unreachable(parent);
  }

  q = q.order("sort_order").order("name");
  if (query.limit) q = q.limit(query.limit);

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as TaxonomyCacheRow[];
};

function resolveStorage(): TaxonomyStorage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null; // access to localStorage can throw in some privacy modes.
  }
}

const defaultCache = makeTaxonomyCache({
  fetcher: supabaseFetcher,
  storage: resolveStorage(),
});

/**
 * Shared, cached taxonomy read. Concurrent callers for the same query share one
 * fetch; a fresh tab reuses a recent snapshot. Returns superset rows — callers
 * project the fields they render.
 */
export function getTaxonomyItems(query: TaxonomyQuery): Promise<TaxonomyCacheRow[]> {
  return defaultCache.get(query);
}
